import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Navbar from "../components/Navbar";
import {
  AcademicCapIcon,
  CurrencyRupeeIcon,
  UserGroupIcon,
  SparklesIcon,
  HeartIcon,
  RocketLaunchIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';

const fadeUp = {
  hidden: { opacity: 0, y: 60 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: 'easeOut' } },
};

const Career = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
    resume: null
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: files ? files[0] : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: '', message: '' });

    try {
      // Create FormData object
      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        submitData.append(key, formData[key]);
      });

      // Submit to SheetDB
      const response = await fetch('YOUR_SHEETDB_API_ENDPOINT', {
        method: 'POST',
        body: submitData
      });

      if (response.ok) {
        setStatus({
          type: 'success',
          message: 'Application submitted successfully! We will contact you soon.'
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          position: '',
          experience: '',
          resume: null
        });
      } else {
        throw new Error('Failed to submit application');
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Failed to submit application. Please try again later.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-orange-50 to-gray-50 relative overflow-hidden">
        {/* Background Decorative Elements */}
        <div className="absolute w-72 h-72 bg-gray-300 rounded-full opacity-20 top-0 -left-20 blur-3xl"></div>
        <div className="absolute w-96 h-96 bg-orange-300 rounded-full opacity-20 bottom-0 -right-24 blur-3xl"></div>

        {/* Hero Section */}
        <section className="bg-gradient-to-r from-gray-800 via-orange-700 to-gray-800 text-white py-24 relative z-10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,_rgba(255,255,255,0.1)_1px,_transparent_0)] bg-[size:20px_20px]"></div>
          <motion.div
            className="max-w-6xl mx-auto px-4 text-center relative"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Join Our Team</h1>
            <p className="text-xl text-gray-100 max-w-3xl mx-auto mb-8">
              Be part of a dynamic team that's transforming sports infrastructure across India. We're looking for passionate professionals who share our vision of excellence.
            </p>
          </motion.div>
        </section>

        {/* Application Form */}
        <section className="py-20 bg-white relative z-10">
          <div className="max-w-4xl mx-auto px-4">
            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={fadeUp}
              className="bg-gradient-to-br from-gray-50 to-orange-50 p-8 rounded-3xl shadow-2xl border border-gray-100"
            >
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-800 mb-4">Submit Your Application</h2>
                <p className="text-gray-600 text-lg">
                  We're always looking for talented individuals to join our team. Submit your application and we'll contact you when relevant positions become available.
                </p>
              </div>

              {status.message && (
                <div className={`p-4 rounded-lg text-center font-medium ${
                  status.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {status.message}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-gray-700 font-medium mb-2">Full Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                      placeholder="Enter your full name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-gray-700 font-medium mb-2">Email</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                      placeholder="Enter your email"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-gray-700 font-medium mb-2">Phone</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                      placeholder="Enter your phone number"
                    />
                  </div>
                  <div>
                    <label htmlFor="position" className="block text-gray-700 font-medium mb-2">Position</label>
                    <select
                      id="position"
                      name="position"
                      value={formData.position}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                    >
                      <option value="">Select a position</option>
                      <option value="Project Manager">Project Manager</option>
                      <option value="Site Engineer">Site Engineer</option>
                      <option value="Quality Control">Quality Control</option>
                      <option value="Sales Executive">Sales Executive</option>
                      <option value="Administrative">Administrative</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="experience" className="block text-gray-700 font-medium mb-2">Experience</label>
                    <select
                      id="experience"
                      name="experience"
                      value={formData.experience}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                    >
                      <option value="">Select experience</option>
                      <option value="Fresher">Fresher</option>
                      <option value="1-3 years">1-3 years</option>
                      <option value="3-5 years">3-5 years</option>
                      <option value="5+ years">5+ years</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="resume" className="block text-gray-700 font-medium mb-2">Resume</label>
                    <input
                      type="file"
                      name="resume"
                      onChange={handleChange}
                      required
                      accept=".pdf,.doc,.docx"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      Accepted formats: PDF, DOC, DOCX (Max size: 5MB)
                    </p>
                  </div>
                </div>

                <div className="flex justify-center">
                  <button
                    type="submit"
                    disabled={loading}
                    className={`inline-flex items-center justify-center gap-2 bg-green-800 text-white px-8 py-3 rounded-lg font-semibold hover:bg-green-900 transition-all duration-300 ${
                      loading ? 'opacity-75 cursor-not-allowed' : ''
                    }`}
                  >
                    {loading ? 'Submitting...' : 'Submit Application'}
                    {!loading && <EnvelopeIcon className="w-5 h-5" />}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        </section>

        {/* Why Join Us */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-orange-50 relative z-10">
          <div className="max-w-6xl mx-auto px-4">
            <motion.h2
              className="text-4xl font-bold text-center mb-14 text-gray-800"
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={fadeUp}
            >
              Why Join Sports Experts Infra?
            </motion.h2>
            <div className="grid md:grid-cols-3 gap-10">
              {[
                {
                  icon: <RocketLaunchIcon className="w-8 h-8 text-gray-600" />,
                  title: "Growth Opportunities",
                  desc: "Accelerate your career with continuous learning, mentorship programs, and clear growth paths.",
                },
                {
                  icon: <HeartIcon className="w-8 h-8 text-gray-600" />,
                  title: "Work-Life Balance",
                  desc: "Enjoy flexible work arrangements, wellness programs, and a supportive work environment.",
                },
                {
                  icon: <UserGroupIcon className="w-8 h-8 text-gray-600" />,
                  title: "Collaborative Culture",
                  desc: "Work with diverse teams in an inclusive environment that values innovation and teamwork.",
                },
                {
                  icon: <SparklesIcon className="w-8 h-8 text-gray-600" />,
                  title: "Innovation Focus",
                  desc: "Be part of a team that's pushing boundaries in sports infrastructure development.",
                },
                {
                  icon: <AcademicCapIcon className="w-8 h-8 text-gray-600" />,
                  title: "Learning & Development",
                  desc: "Access to training programs, workshops, and industry certifications.",
                },
                {
                  icon: <CurrencyRupeeIcon className="w-8 h-8 text-gray-600" />,
                  title: "Competitive Benefits",
                  desc: "Attractive compensation, health insurance, and performance-based rewards.",
                }
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  className="bg-white p-8 rounded-xl shadow-md hover:scale-105 transition-all duration-300 border border-gray-100"
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true }}
                  variants={fadeUp}
                >
                  <div className="mb-4">{item.icon}</div>
                  <h3 className="text-xl font-semibold text-gray-700 mb-3">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Career;
